import type { Permission } from '@/lib/permissions';

/**
 * The screen registry.
 *
 * Transcribed from the `NAV` and `INDEX` arrays in `design/MedOS.dc.html`, which
 * are the design's own source of truth for what exists and where it sits.
 *
 * This file is the ONE shared file feature branches edit. Adding a screen is a
 * single entry here plus a route module under `src/features/<epic>/routes.tsx` -
 * so eleven branches touching eleven features collide on at most eleven lines
 * of this array rather than on the router itself.
 *
 * `permission` is a UI affordance only. The server re-checks every one of these
 * on the request that would perform the action (RBAC-004); hiding a link is
 * never the control that stops access.
 */

export type ScreenId =
  // Auth and activation
  | 'login'
  | 'mfa'
  | 'forgot'
  | 'register'
  | 'onboarding'
  // Public (mobile)
  | 'publicBooking'
  | 'publicConfirm'
  | 'tv'
  // Daily operations
  | 'dashReception'
  | 'dashDoctor'
  | 'dashAdmin'
  | 'appointments'
  | 'week'
  | 'arrival'
  | 'waiting'
  | 'doctorQueue'
  // Patients
  | 'patients'
  | 'patient'
  | 'patientMerge'
  // Clinical
  | 'visit'
  | 'prescription'
  | 'followup'
  | 'prints'
  // Diagnostics
  | 'lab'
  | 'labResult'
  | 'imaging'
  | 'imagingReport'
  | 'diagnosticCases'
  | 'diagnosticCase'
  | 'pharmacy'
  | 'dispense'
  // Finance
  | 'checkout'
  | 'invoices'
  | 'invoice'
  | 'payments'
  | 'expenses'
  | 'reports'
  | 'statement'
  | 'financeDash'
  // Administration
  | 'users'
  | 'doctors'
  | 'branches'
  | 'services'
  | 'settings'
  | 'subscription'
  | 'reference'
  | 'ai'
  | 'audit'
  // Platform
  | 'platformTenants'
  | 'platformTenant'
  | 'platformHealth'
  | 'platformLeads'
  | 'stateGallery';

/** Screens rendered without the application shell (full-bleed). */
export const FULL_BLEED: ReadonlySet<ScreenId> = new Set<ScreenId>([
  'login',
  'mfa',
  'forgot',
  'register',
  'onboarding',
  'publicBooking',
  'publicConfirm',
  'tv',
]);

/** Screens reachable without authentication. */
export const PUBLIC_SCREENS: ReadonlySet<ScreenId> = new Set<ScreenId>([
  'login',
  'mfa',
  'forgot',
  'register',
  'publicBooking',
  'publicConfirm',
]);

export interface NavItem {
  id: ScreenId;
  path: string;
  /** Arabic label. English comes from the i18n catalogue under the same key. */
  label: string;
  labelKey: string;
  icon: string;
  /**
   * Permission that reveals the item; with a list, any one of them does.
   * Omitted means "any authenticated user".
   */
  permission?: Permission | readonly Permission[];
  /** Starts a new visual group in the sidebar. */
  separator?: boolean;
  /** Badge source key; resolved at render time from live counts. */
  badgeKey?: 'appointmentsToday' | 'waiting' | 'lab' | 'imaging' | 'readyCheckout';
}

/**
 * Sidebar order, matching `NAV` in the design.
 *
 * Visibility is derived from permissions rather than from a hard-coded role
 * list: a receptionist granted `finance.read` should see the finance entry
 * without anyone editing this file.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashReception',
    path: '/dashboard/reception',
    label: 'لوحة اليوم',
    labelKey: 'nav.dashReception',
    icon: '◱',
    permission: 'waiting_room.read',
  },
  {
    id: 'dashDoctor',
    path: '/dashboard/doctor',
    label: 'لوحة الطبيب',
    labelKey: 'nav.dashDoctor',
    icon: '◱',
    permission: 'queue.read',
  },
  {
    id: 'appointments',
    path: '/appointments',
    label: 'المواعيد',
    labelKey: 'nav.appointments',
    icon: '▤',
    // A doctor holds only the self-scoped read, and the screen serves both.
    permission: ['appointments.read', 'appointments.read.own'],
    badgeKey: 'appointmentsToday',
  },
  {
    id: 'waiting',
    path: '/waiting-room',
    label: 'غرفة الانتظار',
    labelKey: 'nav.waiting',
    icon: '◷',
    permission: 'waiting_room.read',
    badgeKey: 'waiting',
  },
  {
    id: 'patients',
    path: '/patients',
    label: 'المرضى',
    labelKey: 'nav.patients',
    icon: '☺',
    permission: 'patients.read',
  },
  {
    id: 'visit',
    path: '/visits',
    label: 'مركز الزيارة',
    labelKey: 'nav.visit',
    icon: '✎',
    permission: 'visits.create',
  },
  {
    id: 'lab',
    path: '/lab',
    label: 'المختبر',
    labelKey: 'nav.lab',
    icon: '⚗',
    permission: 'lab.orders.read',
    badgeKey: 'lab',
  },
  {
    id: 'imaging',
    path: '/imaging',
    label: 'الأشعة',
    labelKey: 'nav.imaging',
    icon: '◎',
    permission: 'imaging.orders.read',
    badgeKey: 'imaging',
  },
  {
    // EPIC DXC. Sits with the diagnostics group rather than with appointments,
    // because it is the same kind of work - a sample and a study - reached by a
    // different door. Reception holds the permission; lab and radiology see the
    // entry too, which is deliberate: it is where the referral context lives.
    id: 'diagnosticCases',
    path: '/diagnostic-cases',
    label: 'الحالات التشخيصية',
    labelKey: 'nav.diagnosticCases',
    icon: '⌾',
    permission: 'diagnostic_cases.read',
  },
  {
    id: 'pharmacy',
    path: '/pharmacy',
    label: 'الصيدلية',
    labelKey: 'nav.pharmacy',
    icon: '⊞',
    permission: 'pharmacy.inventory.read',
  },
  {
    id: 'checkout',
    path: '/checkout',
    label: 'المحاسبة',
    labelKey: 'nav.checkout',
    icon: '₪',
    permission: 'checkout.perform',
    badgeKey: 'readyCheckout',
  },
  {
    id: 'invoices',
    path: '/invoices',
    label: 'الفواتير',
    labelKey: 'nav.invoices',
    icon: '▦',
    permission: 'invoices.read',
  },
  {
    id: 'expenses',
    path: '/expenses',
    label: 'المصروفات',
    labelKey: 'nav.expenses',
    icon: '−',
    permission: 'expenses.read',
  },
  {
    id: 'reports',
    path: '/reports',
    label: 'التقارير',
    labelKey: 'nav.reports',
    icon: '◫',
    permission: 'reports.finance.read',
  },

  {
    id: 'users',
    path: '/admin/users',
    label: 'المستخدمون',
    labelKey: 'nav.users',
    icon: '⚇',
    permission: 'staff.read',
    separator: true,
  },
  {
    id: 'doctors',
    path: '/admin/doctors',
    label: 'الأطباء والجداول',
    labelKey: 'nav.doctors',
    icon: '✚',
    permission: 'doctors.write',
  },
  {
    id: 'branches',
    path: '/admin/branches',
    label: 'الفروع',
    labelKey: 'nav.branches',
    icon: '⌂',
    permission: 'branches.write',
  },
  {
    id: 'services',
    path: '/admin/services',
    label: 'الخدمات والأسعار',
    labelKey: 'nav.services',
    icon: '≡',
    permission: 'services.write',
  },
  {
    id: 'reference',
    path: '/admin/reference',
    label: 'المرجع الطبي',
    labelKey: 'nav.reference',
    icon: '✧',
    permission: 'reference.read',
  },
  {
    id: 'ai',
    path: '/admin/ai',
    label: 'المساعد الذكي',
    labelKey: 'nav.ai',
    icon: '✦',
    permission: 'ai.use',
  },
  {
    // The checklist existed and rendered, but nothing linked to it: registration
    // redirects to /login, login lands on the dashboard, and this was reachable
    // only from the "كل الشاشات" index. So a new owner never saw it, and then
    // met "الفرع مغلق" at the first booking with nothing explaining why.
    // Full-bleed by design (see OnboardingScreen) - the link leaves the shell.
    id: 'onboarding',
    path: '/onboarding',
    label: 'قائمة التهيئة',
    labelKey: 'nav.onboarding',
    icon: '☑',
    permission: 'org.settings.read',
  },
  {
    id: 'settings',
    path: '/admin/settings',
    label: 'الإعدادات',
    labelKey: 'nav.settings',
    icon: '⚙',
    permission: 'org.settings.read',
  },
  {
    id: 'subscription',
    path: '/admin/subscription',
    label: 'الاشتراك والباقة',
    labelKey: 'nav.subscription',
    icon: '◈',
    permission: 'subscription.read',
  },
  {
    id: 'audit',
    path: '/admin/audit',
    label: 'سجل التدقيق',
    labelKey: 'nav.audit',
    icon: '⎙',
    permission: 'audit.read',
  },
] as const;

/** Platform-operator sidebar. Only reachable with `User.isPlatformAdmin`. */
export const PLATFORM_NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'platformTenants',
    path: '/platform/tenants',
    label: 'العيادات المشتركة',
    labelKey: 'nav.platformTenants',
    icon: '⛁',
  },
  {
    id: 'platformHealth',
    path: '/platform/health',
    label: 'صحة المنصة',
    labelKey: 'nav.platformHealth',
    icon: '♥',
  },
  {
    id: 'platformLeads',
    path: '/platform/leads',
    label: 'طلبات العروض',
    labelKey: 'nav.platformLeads',
    icon: '✉',
  },
  {
    id: 'subscription',
    path: '/platform/plans',
    label: 'الباقات والحدود',
    labelKey: 'nav.plans',
    icon: '◈',
  },
  {
    id: 'audit',
    path: '/platform/audit',
    label: 'تدقيق المنصة',
    labelKey: 'nav.platformAudit',
    icon: '⎙',
  },
] as const;

/**
 * The "all screens" index, matching `INDEX` in the design.
 *
 * Kept because it is genuinely useful during build-out and pilot: it is the
 * only surface that lists every screen including the ones with no nav entry
 * (patient detail, invoice detail, arrival, TV mode).
 */
export const SCREEN_INDEX: readonly {
  titleKey: string;
  title: string;
  items: readonly { id: ScreenId; path: string; label: string }[];
}[] = [
  {
    titleKey: 'index.auth',
    title: 'الدخول والتفعيل',
    items: [
      { id: 'login', path: '/login', label: 'تسجيل الدخول' },
      { id: 'mfa', path: '/login/mfa', label: 'التحقق الثنائي' },
      { id: 'forgot', path: '/forgot-password', label: 'استعادة كلمة المرور' },
      { id: 'register', path: '/register', label: 'تسجيل عيادة جديدة' },
      { id: 'onboarding', path: '/onboarding', label: 'قائمة التهيئة' },
    ],
  },
  {
    titleKey: 'index.public',
    title: 'الحجز العام (جوال)',
    items: [
      { id: 'publicBooking', path: '/book/:slug', label: 'الحجز العام' },
      { id: 'publicConfirm', path: '/book/:slug/confirmed', label: 'تأكيد الحجز' },
    ],
  },
  {
    titleKey: 'index.operations',
    title: 'التشغيل اليومي',
    items: [
      { id: 'dashReception', path: '/dashboard/reception', label: 'لوحة الاستقبال' },
      { id: 'dashDoctor', path: '/dashboard/doctor', label: 'لوحة الطبيب' },
      { id: 'appointments', path: '/appointments', label: 'المواعيد — قائمة' },
      { id: 'week', path: '/appointments/week', label: 'التقويم الأسبوعي' },
      { id: 'arrival', path: '/appointments/:id/arrival', label: 'تسجيل الوصول' },
      { id: 'waiting', path: '/waiting-room', label: 'غرفة الانتظار' },
      { id: 'tv', path: '/waiting-room/tv', label: 'شاشة الانتظار العامة' },
      { id: 'doctorQueue', path: '/queue', label: 'طابور الطبيب' },
    ],
  },
  {
    titleKey: 'index.patients',
    title: 'المرضى',
    items: [
      { id: 'patients', path: '/patients', label: 'سجل المرضى' },
      { id: 'patient', path: '/patients/:id', label: 'ملف المريض' },
      { id: 'patientMerge', path: '/patients/merge', label: 'دمج ملفين' },
    ],
  },
  {
    titleKey: 'index.clinical',
    title: 'السريري',
    items: [
      { id: 'visit', path: '/visits/:id', label: 'مركز الزيارة' },
      { id: 'prescription', path: '/visits/:id/prescription', label: 'الوصفة والفحص الدوائي' },
      { id: 'followup', path: '/follow-ups', label: 'خطة المتابعة' },
      { id: 'prints', path: '/documents/print', label: 'الوثائق والطباعة' },
    ],
  },
  {
    titleKey: 'index.diagnostics',
    title: 'التشخيص',
    items: [
      { id: 'lab', path: '/lab', label: 'قائمة عمل المختبر' },
      { id: 'labResult', path: '/lab/:id/result', label: 'إدخال نتيجة + قيمة حرجة' },
      { id: 'imaging', path: '/imaging', label: 'قائمة عمل الأشعة' },
      { id: 'imagingReport', path: '/imaging/:id/report', label: 'محرر تقرير الأشعة' },
      { id: 'pharmacy', path: '/pharmacy', label: 'المخزون الدوائي' },
      { id: 'dispense', path: '/pharmacy/dispense', label: 'صرف دواء' },
    ],
  },
  {
    titleKey: 'index.finance',
    title: 'المالية',
    items: [
      { id: 'checkout', path: '/checkout', label: 'المحاسبة والدفع' },
      { id: 'invoices', path: '/invoices', label: 'الفواتير' },
      { id: 'invoice', path: '/invoices/:id', label: 'تفاصيل فاتورة' },
      { id: 'payments', path: '/payments', label: 'المدفوعات والاسترداد' },
      { id: 'expenses', path: '/expenses', label: 'المصروفات' },
      { id: 'reports', path: '/reports', label: 'التقارير المالية' },
      { id: 'statement', path: '/patients/:id/statement', label: 'كشف حساب مريض' },
      { id: 'financeDash', path: '/reports/dashboard', label: 'لوحة المالية' },
    ],
  },
  {
    titleKey: 'index.admin',
    title: 'الإدارة',
    items: [
      { id: 'users', path: '/admin/users', label: 'المستخدمون' },
      { id: 'doctors', path: '/admin/doctors', label: 'الأطباء والجداول' },
      { id: 'branches', path: '/admin/branches', label: 'الفروع' },
      { id: 'services', path: '/admin/services', label: 'الخدمات والأسعار' },
      { id: 'settings', path: '/admin/settings', label: 'إعدادات العيادة' },
      { id: 'subscription', path: '/admin/subscription', label: 'الاشتراك والاستخدام' },
      { id: 'reference', path: '/admin/reference', label: 'المرجع الطبي' },
      { id: 'ai', path: '/admin/ai', label: 'ضوابط الذكاء الاصطناعي' },
      { id: 'audit', path: '/admin/audit', label: 'سجل التدقيق' },
    ],
  },
  {
    titleKey: 'index.platform',
    title: 'منصة SaaS',
    items: [
      { id: 'platformTenants', path: '/platform/tenants', label: 'قائمة المستأجرين' },
      { id: 'platformTenant', path: '/platform/tenants/:id', label: 'تفاصيل مستأجر' },
      { id: 'platformHealth', path: '/platform/health', label: 'صحة الخدمات' },
      { id: 'platformLeads', path: '/platform/leads', label: 'طلبات العروض' },
    ],
  },
  {
    titleKey: 'index.states',
    title: 'حالات النظام',
    items: [
      { id: 'stateGallery', path: '/dev/states', label: 'فارغ · تحميل · خطأ · تعارض · حد الباقة' },
    ],
  },
] as const;
